/**
 * AudioEdgeSilence — detect leading / trailing silence in a decoded
 * AudioBuffer.
 *
 * Origin: /audio-looper telemetry (2026-05) showed crossfade used in only
 * 3.7% of completed loops, and a user comment ("как сделать чтобы паузы
 * были минимальны?") traced to the canonical cause — the source file has
 * quiet lead-in / lead-out, and looping it stacks that dead space between
 * iterations as an audible pause. The tool had no way to see or act on
 * edge silence. This module is the shared detection primitive; the looper
 * (and any other tool — trim, ringtone) consumes it.
 *
 *   var es = window.AudioEdgeSilence.detect(audioBuffer, {
 *       relDb: -40,       // threshold = body level + relDb (adaptive)
 *       minRunMs: 60,     // signal must SUSTAIN this long to count
 *       thresholdDb: null // set to force an absolute threshold instead
 *   });
 *   // → { leadSec, trailSec, leadSamples, trailSamples, hasSilence,
 *   //     allSilent, coreSec, durationSec, thresholdDb, bodyDb, minRunMs }
 *
 * ── Why v2 (2026-08-17, comment #1408 «нажал Убрать паузу — пауза осталась») ──
 * v1 scanned 5 ms windows inward and stopped at the FIRST window whose peak
 * exceeded an ABSOLUTE −45 dBFS. Two measured failure modes:
 *
 *  1. ONE STRAY BLIP HALTS THE TRIM. A single 5 ms click sitting inside the
 *     trailing silence (edit artefact, tape/vinyl rip, encoder hiccup) ends
 *     the scan there, so only the silence AFTER the click gets trimmed.
 *     Reproduced end-to-end: a file with 1.5 s silence + click + 1.5 s
 *     silence trimmed 1.5 s of 3.0 s, and the recorded PREVIEW then carried
 *     1200 ms holes between repeats — exactly the report. Fix: a window only
 *     counts as signal when the signal SUSTAINS for `minRunMs`; isolated
 *     blips are treated as part of the dead air. Measured 60 ms: kills the
 *     click (material cut beyond v1: −58 dB rel. body = inaudible) while a
 *     90 ms pickup note survives (120 ms would have eaten it at −11 dB rel.
 *     body — plainly audible).
 *
 *  2. ABSOLUTE THRESHOLD PUNISHES QUIET MATERIAL. −45 dBFS is ~−38 dB below
 *     a loud master (fine) but only ~−20 dB below a quiet ambient recording,
 *     so it ate audible decay tails: on a −25 dBFS piano piece v1 cut 40 ms
 *     of tail whose level was −25 dB rel. body. Fix: derive the threshold
 *     from the material — `body + relDb`, clamped to [floorDb, ceilDb] so
 *     neither a brickwalled master nor a whisper-quiet take goes off-scale.
 *     Callers that need the old behaviour pass `thresholdDb` explicitly.
 *
 * Energy-fraction detection was also measured and REJECTED: it cut 1.24 s of
 * a real track's fade-out and 0.55 s of a piano tail — a silent quality loss.
 *
 * Bench + fixtures: tests/scratch/looper-silence/ (algo-bench, module-bench).
 */
(function() {
'use strict';

var WINDOW_SEC = 0.005;
var DEFAULTS = {
    relDb: -40,        // threshold sits this far below the body level
    floorDb: -60,      // never demand quieter than this (dither/noise floor)
    ceilDb: -35,       // never call anything above this "silence"
    minRunMs: 60,      // sustained signal required to end the silence
    maxScanSec: 60     // don't scan deeper than this from either edge
};

/**
 * Body level = RMS of the loudest half of 0.5 s frames, i.e. "how loud is
 * this material when it is playing". Median-of-loud-half rather than a
 * global RMS so long quiet passages don't drag the reference down. Sampled
 * (≤128 frames, every 8th sample) — this only sets a threshold, and the
 * full-precision version cost ~10× for an identical answer on the corpus.
 */
function computeBodyDb(chans, len, sr) {
    var frame = Math.max(1, Math.floor(sr * 0.5));
    var nFrames = Math.floor(len / frame);
    if (nFrames < 1) { frame = len; nFrames = 1; }
    var step = Math.max(1, Math.floor(nFrames / 128));
    var vals = [];
    for (var fi = 0; fi < nFrames; fi += step) {
        var s0 = fi * frame, s1 = Math.min(len, s0 + frame);
        var acc = 0, n = 0;
        for (var ci = 0; ci < chans.length; ci++) {
            var d = chans[ci];
            for (var s = s0; s < s1; s += 8) { acc += d[s] * d[s]; n++; }
        }
        if (n) vals.push(Math.sqrt(acc / n));
    }
    if (!vals.length) return -Infinity;
    vals.sort(function(a, b) { return b - a; });
    var top = vals.slice(0, Math.max(1, Math.floor(vals.length / 2)));
    var sum = 0;
    for (var i = 0; i < top.length; i++) sum += top[i];
    var rms = sum / top.length;
    return rms > 0 ? 20 * Math.log10(rms) : -Infinity;
}

function detect(audioBuffer, opts) {
    opts = opts || {};
    var relDb      = (opts.relDb      != null) ? opts.relDb      : DEFAULTS.relDb;
    var floorDb    = (opts.floorDb    != null) ? opts.floorDb    : DEFAULTS.floorDb;
    var ceilDb     = (opts.ceilDb     != null) ? opts.ceilDb     : DEFAULTS.ceilDb;
    var minRunMs   = (opts.minRunMs   != null) ? opts.minRunMs   : DEFAULTS.minRunMs;
    var maxScanSec = (opts.maxScanSec != null) ? opts.maxScanSec : DEFAULTS.maxScanSec;

    if (!audioBuffer || !audioBuffer.length || !audioBuffer.numberOfChannels) {
        return {
            leadSec: 0, trailSec: 0, leadSamples: 0, trailSamples: 0,
            hasSilence: false, allSilent: false, coreSec: 0,
            durationSec: 0, thresholdDb: (opts.thresholdDb != null ? opts.thresholdDb : -45),
            bodyDb: -Infinity, minRunMs: minRunMs
        };
    }

    var sr = audioBuffer.sampleRate;
    var len = audioBuffer.length;
    var nch = audioBuffer.numberOfChannels;

    // Hoist channel arrays once — getChannelData() per window would be
    // a needless allocation on every iteration.
    var chans = [];
    for (var c = 0; c < nch; c++) chans.push(audioBuffer.getChannelData(c));

    var bodyDb = computeBodyDb(chans, len, sr);
    var thresholdDb;
    if (opts.thresholdDb != null) {
        thresholdDb = opts.thresholdDb;          // caller forces an absolute gate
    } else if (bodyDb === -Infinity) {
        thresholdDb = -45;                        // digital-black file
    } else {
        thresholdDb = bodyDb + relDb;
        if (thresholdDb < floorDb) thresholdDb = floorDb;
        if (thresholdDb > ceilDb)  thresholdDb = ceilDb;
        // Round for readability — this value lands in telemetry and, one day,
        // possibly in UI; 0.1 dB is far finer than the decision it drives.
        thresholdDb = Math.round(thresholdDb * 10) / 10;
    }
    var threshold = Math.pow(10, thresholdDb / 20); // linear amplitude

    var win = Math.max(1, Math.floor(sr * WINDOW_SEC));
    var maxScan = Math.min(len, Math.ceil(maxScanSec * sr));
    // A blip shorter than this is dead air, not the start/end of content.
    var needRuns = Math.max(1, Math.round((minRunMs / 1000) * sr / win));

    function windowLoud(from, to) {
        for (var ci = 0; ci < chans.length; ci++) {
            var d = chans[ci];
            for (var s = from; s < to; s++) {
                var a = d[s] < 0 ? -d[s] : d[s];
                if (a > threshold) return true;
            }
        }
        return false;
    }

    // ── Leading silence ──
    // Walk forward; a loud window only ENDS the silence when the following
    // `needRuns` windows are loud too (sustained content, not a click).
    var leadSamples = 0;
    var scannedLead = 0;
    for (var i = 0; i < len && scannedLead < maxScan; i += win, scannedLead += win) {
        var endL = i + win; if (endL > len) endL = len;
        if (windowLoud(i, endL)) {
            var runL = 0, jL = i;
            while (runL < needRuns && jL < len) {
                var e2 = jL + win; if (e2 > len) e2 = len;
                if (!windowLoud(jL, e2)) break;
                runL++; jL = e2;
            }
            if (runL >= needRuns || jL >= len) break;   // real content starts here
        }
        leadSamples = endL;
    }

    // Whole file below threshold — degenerate (silent / near-silent file).
    // Report it so the caller can decline to "trim" everything.
    if (leadSamples >= len) {
        return {
            leadSec: len / sr, trailSec: 0,
            leadSamples: len, trailSamples: 0,
            hasSilence: true, allSilent: true, coreSec: 0,
            durationSec: len / sr, thresholdDb: thresholdDb,
            bodyDb: bodyDb, minRunMs: minRunMs
        };
    }

    // ── Trailing silence ── (scan backward, never past leadSamples)
    var trailSamples = 0;
    var scannedTrail = 0;
    for (var k = len; k > leadSamples && scannedTrail < maxScan; k -= win, scannedTrail += win) {
        var startT = k - win; if (startT < leadSamples) startT = leadSamples;
        if (windowLoud(startT, k)) {
            var runT = 0, jT = k;
            while (runT < needRuns && jT > leadSamples) {
                var s2 = jT - win; if (s2 < leadSamples) s2 = leadSamples;
                if (!windowLoud(s2, jT)) break;
                runT++; jT = s2;
            }
            if (runT >= needRuns || jT <= leadSamples) break;   // real content ends here
        }
        trailSamples = len - startT;
    }

    var coreSamples = len - leadSamples - trailSamples;
    return {
        leadSec: leadSamples / sr,
        trailSec: trailSamples / sr,
        leadSamples: leadSamples,
        trailSamples: trailSamples,
        coreSec: coreSamples / sr,
        hasSilence: (leadSamples + trailSamples) > 0,
        allSilent: false,
        durationSec: len / sr,
        thresholdDb: thresholdDb,
        bodyDb: bodyDb,
        minRunMs: minRunMs
    };
}

window.AudioEdgeSilence = {
    detect: detect,
    version: '2.0.0'
};

})();
